import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import TypewriterText from './TypewriterText';

const Hero = ({ profile }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  if (!profile) return null;

  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-primary-50/20 to-white dark:from-neutral-950 dark:via-primary-900/10 dark:to-neutral-950 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-accent-500 dark:from-primary-400 dark:to-accent-400 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-300 animate-pulse"></div>
              <div className="relative">
                <img
                  src="/profile.jpg"
                  alt="Mohammad Cordova"
                  className={`w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 rounded-full object-cover border-4 border-white dark:border-neutral-900 shadow-2xl transition-all duration-300 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {!imageLoaded && (
                  <div className="w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 rounded-full bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 animate-pulse"></div>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-neutral-900 dark:text-neutral-50 mb-6">
              Mohammad <span className="text-accent-600 dark:text-accent-400">Cordova</span>
            </h1>
            <div className="text-xl sm:text-2xl md:text-3xl text-neutral-600 dark:text-neutral-300 font-medium mb-8 min-h-[2.5rem] md:min-h-[3rem]">
              <TypewriterText text={profile.title} delay={80} />
            </div>
            <p className="text-base md:text-lg text-neutral-500 dark:text-neutral-400 max-w-2xl mx-auto mb-12 leading-relaxed">
              Designing, developing, and maintaining information systems with expertise in 
              logistics, inventory management, and IT infrastructure.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <a
              href="#projects"
              className="px-8 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl hover:shadow-primary-500/50 dark:hover:shadow-primary-400/30"
            >
              View Projects
            </a>
            <a
              href="#contact"
              className="px-8 py-3 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50 border-2 border-neutral-200 dark:border-neutral-700 rounded-lg font-medium hover:border-primary-600 dark:hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Get in Touch
            </a>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            onClick={scrollToAbout}
            className="mt-20 inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-neutral-300 dark:border-neutral-700 hover:border-primary-600 dark:hover:border-primary-400 text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors animate-bounce"
            aria-label="Scroll to about section"
          >
            <ChevronDown className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
